/**
 * Touch diagnostics (employer request 2026-10-15): a recorder the member turns on from the command
 * palette (or `?diag=1`) to find out, on the phone itself, why a tap answers late or lands on what
 * was under it. Timings only, kept in memory: no text from the page, ids in paths become `:id`, and
 * nothing is sent anywhere. «کپی گزارش» puts the record on the clipboard for the member to send.
 */

const MAX_LINES = 600;
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const CONTROL = 'a, button, input, select, textarea, summary, label, [role=button], [role=tab], [role=option], [role=switch]';

class Recorder {
  on = $state(false);
  count = $state(0);
}
export const diag = new Recorder();

let lines: string[] = [];
let t0 = 0;
let stopAll: (() => void) | null = null;

/** `/o/0190…/patients/0190…?x=0190…` keeps its shape without the ids. */
export const maskPath = (path: string): string => path.replace(UUID, ':id');
const ms = (t: number) => Math.round(t - t0);

/**
 * What was touched, without any of its text: the element and its role, the control it is part of,
 * a link's path shape, the nearest named part of the page (data-tour / data-testid), and whether it
 * sits in a dialog.
 */
export function describe(target: EventTarget | null): string {
  if (!(target instanceof Element)) return target instanceof Node ? target.nodeName.toLowerCase() : 'none';
  const tag = (e: Element) => {
    const role = e.getAttribute('role');
    return e.tagName.toLowerCase() + (role ? `[${role}]` : '');
  };
  const parts = [tag(target)];
  const control = target.closest(CONTROL);
  if (control && control !== target) parts.push(tag(control));
  if (control instanceof HTMLAnchorElement && control.href) {
    parts.push(`href=${maskPath(new URL(control.href, window.location.href).pathname)}`);
  }
  const named = target.closest('[data-tour], [data-testid]');
  if (named) parts.push(`{${named.getAttribute('data-tour') ?? named.getAttribute('data-testid')}}`);
  const dialog = target.closest('dialog');
  if (dialog) parts.push(!dialog.open ? 'in-closed-dialog' : dialog.matches(':modal') ? 'in-modal' : 'in-dialog');
  if (target.closest('[popover]')) parts.push('in-popover');
  return parts.join(' ');
}

/** The top layer right now: open dialogs, how many of them are modal, and open popovers. */
export function layers(doc: Document = document): string {
  const dialogs = [...doc.querySelectorAll('dialog[open]')];
  const modal = dialogs.filter((d) => d.matches(':modal')).length;
  let popovers: number;
  try {
    popovers = doc.querySelectorAll(':popover-open').length;
  } catch {
    popovers = -1;
  }
  return `dialogs=${dialogs.length} modal=${modal} popovers=${popovers}`;
}

/**
 * One finished call to the API as a line: how long it took, how long it waited before it was sent
 * (a new connection, or a queue), the wait for the first byte, whether it opened a connection (and
 * what that cost) or reused one, the protocol and the status. Path only (ids masked), never a query
 * string. Anything that is not the app's own /v1 is null.
 */
export function describeResource(r: PerformanceResourceTiming, origin: string = window.location.origin): string | null {
  let url: URL;
  try {
    url = new URL(r.name);
  } catch {
    return null;
  }
  if (url.origin !== origin || !url.pathname.startsWith('/v1/')) return null;
  const part = (from: number, to: number) => Math.max(0, Math.round(to - from));
  const opened = r.connectEnd > r.connectStart;
  const tls = r.secureConnectionStart > 0 ? part(r.secureConnectionStart, r.connectEnd) : 0;
  const connection = opened
    ? `new(dns=${part(r.domainLookupStart, r.domainLookupEnd)} tcp=${part(r.connectStart, r.secureConnectionStart > 0 ? r.secureConnectionStart : r.connectEnd)} tls=${tls})`
    : 'reused';
  const status = r.responseStatus ? ` ${r.responseStatus}` : '';
  return `${maskPath(url.pathname)} dur=${Math.round(r.duration)} wait=${part(r.startTime, r.requestStart)} first-byte=${part(r.requestStart, r.responseStart)} ${connection} ${r.nextHopProtocol || '?'}${status} at=${ms(r.startTime)}`;
}

/**
 * The system's safe-area insets as the page sees them (top/right/bottom/left, px): what an installed
 * app leaves free of the status bar, the gesture bar and the cutout. 0/0/0/0 means the page reaches
 * the screen's edges, or the browser reports none.
 */
export function safeInsets(doc: Document = document): string {
  try {
    const probe = doc.createElement('div');
    probe.style.cssText =
      'position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
    doc.body.append(probe);
    const s = getComputedStyle(probe);
    const px = [s.paddingTop, s.paddingRight, s.paddingBottom, s.paddingLeft].map((v) => Math.round(Number.parseFloat(v) || 0));
    probe.remove();
    return px.join('/');
  } catch {
    return '?';
  }
}

/** What the browser says about the connection (not on every browser): type, round trip, speed, data saver. */
function connectionInfo(): string {
  const c = (navigator as Navigator & { connection?: { effectiveType?: string; rtt?: number; downlink?: number; saveData?: boolean } }).connection;
  return c ? `${c.effectiveType ?? '?'}/${c.rtt ?? '?'}ms/${c.downlink ?? '?'}Mbps${c.saveData ? '/save-data' : ''}` : 'unknown';
}

/** Adds a line while recording (the oldest go first past the limit; the first line stays). */
export function record(kind: string, detail = ''): void {
  if (!diag.on) return;
  if (lines.length >= MAX_LINES) lines.splice(1, 1);
  lines.push(`${ms(performance.now())} ${kind} ${detail}`.trimEnd());
  diag.count = lines.length;
}

export function diagText(): string {
  return lines.join('\n');
}

/** Starts recording, from zero. */
export function startDiag(): void {
  stopDiag();
  t0 = performance.now();
  diag.on = true;
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  lines = [
    `start ${new Date().toISOString()} ua=${navigator.userAgent} standalone=${standalone} sw=${Boolean(navigator.serviceWorker?.controller)} view=${window.innerWidth}x${window.innerHeight} screen=${window.screen.width}x${window.screen.height} safe=${safeInsets()} dpr=${window.devicePixelRatio} path=${maskPath(window.location.pathname)} online=${navigator.onLine} net=${connectionInfo()}`,
  ];
  diag.count = lines.length;

  const offs: (() => void)[] = [];
  const listen = (target: EventTarget, type: string, fn: (e: Event) => void) => {
    target.addEventListener(type, fn, { capture: true, passive: true });
    offs.push(() => target.removeEventListener(type, fn, { capture: true }));
  };
  // How long after the touch the page got to run a handler (a busy page answers late).
  const lag = (e: Event) => Math.round(performance.now() - e.timeStamp);
  let pressed = '';
  listen(document, 'pointerdown', (e) => {
    const p = e as PointerEvent;
    pressed = describe(p.target);
    // `bottom` and `side`: how far from the screen's bottom and nearest side edge the finger landed
    // (the system gesture areas of an installed app sit there).
    const bottom = Math.round(window.innerHeight - p.clientY);
    const side = Math.round(Math.min(p.clientX, window.innerWidth - p.clientX));
    record('down', `${p.pointerType} x=${Math.round(p.clientX)} y=${Math.round(p.clientY)} bottom=${bottom} side=${side} lag=${lag(e)} ${pressed} | ${layers()}`);
  });
  listen(document, 'pointerup', (e) => record('up', `lag=${lag(e)}`));
  listen(document, 'pointercancel', (e) => record('pointercancel', `lag=${lag(e)}`));
  listen(document, 'click', (e) => {
    const what = describe(e.target);
    const at = performance.now();
    record('click', `lag=${lag(e)} ${what}${what === pressed ? '' : ' (not the pressed element)'} | ${layers()}`);
    // When the frame after the click was drawn.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(() => record('frame', `+${Math.round(performance.now() - at)}ms | ${layers()}`)));
    }
  });
  // `cancel`, `close` and `toggle` do not bubble; a capturing listener still sees them.
  listen(document, 'cancel', (e) => record('cancel', `cancelable=${e.cancelable} ${describe(e.target)}`));
  listen(document, 'close', (e) => record('close', describe(e.target)));
  listen(document, 'toggle', (e) => record('toggle', `${(e as ToggleEvent).newState} ${describe(e.target)}`));
  listen(window, 'popstate', () => record('popstate', maskPath(window.location.pathname + window.location.search)));
  listen(document, 'visibilitychange', () => record('visibility', document.visibilityState));
  listen(window, 'pageshow', (e) => record('pageshow', `persisted=${(e as PageTransitionEvent).persisted}`));
  listen(window, 'resize', () => record('resize', `${window.innerWidth}x${window.innerHeight}`));
  // The phone's own view of the network and of the page being put to sleep: a freeze and a change of
  // network are what leave a connection dead.
  listen(window, 'online', () => record('online', connectionInfo()));
  listen(window, 'offline', () => record('offline'));
  listen(document, 'freeze', () => record('freeze'));
  listen(document, 'resume', () => record('resume'));
  const connection = (navigator as Navigator & { connection?: EventTarget }).connection;
  if (connection) listen(connection, 'change', () => record('network-change', connectionInfo()));

  const observe = (type: string, fn: (entry: PerformanceEntry) => void, init: PerformanceObserverInit) => {
    if (typeof PerformanceObserver === 'undefined' || !PerformanceObserver.supportedEntryTypes?.includes(type)) {
      record('no-observer', type);
      return;
    }
    const observer = new PerformanceObserver((list) => list.getEntries().forEach(fn));
    observer.observe(init);
    offs.push(() => observer.disconnect());
  };
  // Slow interactions (Event Timing): the wait before the handler, the handler, the whole until paint.
  observe(
    'event',
    (entry) => {
      const e = entry as PerformanceEventTiming;
      record(
        'slow-event',
        `${e.name} total=${Math.round(e.duration)} wait=${Math.round(e.processingStart - e.startTime)} run=${Math.round(e.processingEnd - e.processingStart)} at=${ms(e.startTime)} ${describe(e.target ?? null)}`,
      );
    },
    { type: 'event', durationThreshold: 40 } as PerformanceObserverInit,
  );
  // Every call to the API: how long, how long it waited, new connection or reused (the phone's own timings).
  observe(
    'resource',
    (entry) => {
      const line = describeResource(entry as PerformanceResourceTiming);
      if (line) record('net', line);
    },
    { type: 'resource' },
  );
  // Long frames, with the scripts that took the time (file, position and duration only).
  observe(
    'long-animation-frame',
    (entry) => {
      const frame = entry as PerformanceEntry & {
        blockingDuration?: number;
        scripts?: { invoker?: string; sourceURL?: string; sourceFunctionName?: string; sourceCharPosition?: number; duration: number }[];
      };
      const scripts = (frame.scripts ?? [])
        .slice(0, 4)
        .map((s) => `${s.invoker ?? '?'}@${(s.sourceURL ?? '').split('/').pop()}:${s.sourceCharPosition ?? ''}:${s.sourceFunctionName ?? ''}=${Math.round(s.duration)}`)
        .join(' ');
      record('long-frame', `dur=${Math.round(frame.duration)} block=${Math.round(frame.blockingDuration ?? 0)} at=${ms(frame.startTime)} ${scripts}`);
    },
    { type: 'long-animation-frame' },
  );

  stopAll = () => offs.forEach((off) => off());
}

export function stopDiag(): void {
  stopAll?.();
  stopAll = null;
  diag.on = false;
}
